import { resolveValueAtTimestamp } from './resolveValueAtTimestamp'
import type { MetricSeries } from './types'

/**
 * How a series' value at the hovered day is classified for the tooltip:
 * `observed` (a real reading), `computed` (carried forward from an earlier
 * reading), or `missing` (nothing to show). `sampledDate` is the day the shown
 * value was actually read.
 */
export type TooltipPoint =
  | { kind: 'observed'; value: number; sampledDate: string }
  | { kind: 'computed'; value: number; sampledDate: string }
  | { kind: 'missing' }

/**
 * Classifies a series' point at `timestampMs` for the tooltip. Uses the backend
 * `source` label as the authoritative signal when present, and falls back to
 * carry-forward inference (is the sampled date earlier than the hovered day?)
 * for series that aren't normalized and carry no `source`, e.g. single-channel.
 *
 * The displayed value always comes from carry-forward — `computed` points carry
 * a null value, so the number and its `sampledDate` are the last observed sample.
 *
 * Pure, mirroring {@link resolveValueAtTimestamp}.
 */
export function getTooltipPoint(
  metric: MetricSeries,
  timestampMs: number,
): TooltipPoint {
  const resolved = resolveValueAtTimestamp(metric, timestampMs)
  const sourceAtDay = metric.data.find(
    (p) => new Date(p.date).getTime() === timestampMs,
  )?.source

  if (sourceAtDay === 'missing' || !resolved) return { kind: 'missing' }

  const carriedForward =
    sourceAtDay != null
      ? sourceAtDay === 'computed'
      : new Date(resolved.sampledDate).getTime() < timestampMs

  return {
    kind: carriedForward ? 'computed' : 'observed',
    value: resolved.value,
    sampledDate: resolved.sampledDate,
  }
}
